// Resource overflow (§33 I4): loose material — a square's scrap heap and the
// units on its ground (§33.1 Q2: a heap is loose) — holds at most
// `RULES.looseCap` (20) a square. The excess spills one unit at a time onto
// the cross at Manhattan distance 1, drawn off `streamFor` — never into void
// or onto a building — and a neighbour pushed past the cap spills on in turn.
//
// One pass at `applyCommand`'s exit (`settleOverflow`) rather than in each of
// the ~30 writers: every door that drops, wrecks or spills is one rule, and
// nothing in a search sees it. Deposits are not loose (the doc's "not in a
// ... deposit") and live in their own map.

import { fileOf, rankOf, square } from "../board";
import { hasCell, type Position } from "../position";
import { streamFor } from "../random";
import { RULES } from "../rules";
import { addHeap, dropOnGround, heapAt, type IndustryState, structureAt } from "./state";

/** The `streamFor` salt overflow draws on. */
export const OVERFLOW_STREAM = 157;

/** Loose units on `sq`: its heap and its ground. */
export const looseAt = (industry: IndustryState, sq: number): number =>
  heapAt(industry, sq) + (industry.dropped.get(sq)?.length ?? 0);

/** Squares holding more loose material than the cap, in board order. */
function overflowing(industry: IndustryState): number[] {
  const squares = new Set<number>([...industry.heap.keys(), ...industry.dropped.keys()]);
  return [...squares].filter((sq) => looseAt(industry, sq) > RULES.looseCap).sort((a, b) => a - b);
}

/** Does anything on this board need spilling? */
export const overflowStale = (industry: IndustryState): boolean => overflowing(industry).length > 0;

/** Where `sq`'s excess may land: the four orthogonal cells without a building. */
function spillTargets(position: Position, industry: IndustryState, sq: number): number[] {
  const f = fileOf(sq);
  const r = rankOf(sq);
  return [square(f, r + 1), square(f + 1, r), square(f, r - 1), square(f - 1, r)].filter(
    (to) => hasCell(position, to) && !structureAt(industry, to)
  );
}

/**
 * Spill every square over the cap, cascading, in place. Ground units go
 * first (newest first), then scrap off the heap, which lands as heap. A
 * square with nowhere to spill keeps its excess.
 */
export function settleOverflow(position: Position, industry: IndustryState, seed: number, ply: number): void {
  const draw = streamFor(seed, ply, OVERFLOW_STREAM);
  // Each pass moves at least a unit or stops; the cap bounds a pathological
  // board the way the Gambit's 64 rounds do.
  for (let pass = 0; pass < 64; pass++) {
    let moved = false;
    for (const sq of overflowing(industry)) {
      const targets = spillTargets(position, industry, sq);
      if (targets.length === 0) continue;
      for (let excess = looseAt(industry, sq) - RULES.looseCap; excess > 0; excess--) {
        const to = targets[Math.floor(draw() * targets.length)];
        const ground = industry.dropped.get(sq);
        if (ground && ground.length > 0) {
          const unit = ground.pop()!;
          if (ground.length === 0) industry.dropped.delete(sq);
          dropOnGround(industry, to, unit);
        } else {
          addHeap(industry, sq, -1);
          addHeap(industry, to, 1);
        }
        moved = true;
      }
    }
    if (!moved) return;
  }
}
